// src/SpreadsheetProcessor.js
import React, { useState } from "react";
import * as XLSX from "xlsx";
//import { saveAs } from "file-saver"; // Import file-saver for downloading files

// Import custom scripts that are defined for each module
import createPrompt from "./soap_scripts/promptcreate.js";

const SpreadsheetProcessor = ({ soapCredentials }) => {
  const [fileData, setFileData] = useState(null);
  const [progress, setProgress] = useState([]); // Track progress messages
  const [loading, setLoading] = useState(false); // Loading state

  // Function to add progress messages with timestamp
  const addProgressMessage = (message, type = "info") => {
    const timestamp = new Date().toLocaleTimeString();
    setProgress(prev => [...prev, { message, type, timestamp }]);
  };

  // Function to handle file upload and reading
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();

    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: "array" });

      // Store the workbook
      setFileData(workbook);
      addProgressMessage("File uploaded successfully. Processing workbook...", "info");
      console.log("Workbook data:", workbook);
    };

    reader.readAsArrayBuffer(file);
  };

  // Function to run the script based on the sheet data
  const processSheetData = async () => {
    if (!fileData) {
      addProgressMessage("Please upload a file first.", "error");
      return;
    }

    // Check if soapCredentials is defined
    if (
      !soapCredentials ||
      !soapCredentials.username ||
      !soapCredentials.password
    ) {
      addProgressMessage("Please provide SOAP API credentials.", "error");
      return;
    }

    setLoading(true); // Start loading state
    setProgress([]); // Reset progress messages

    try {
      const sheets = fileData.SheetNames; // Get all sheet names
      console.log("Sheets:", sheets);

      addProgressMessage("Starting spreadsheet processing...", "info");

      // Define an exclusion list for sheet names
      const exclusionList = [
        "Cover",
        "Revisions",
        "Introduction",
        "Acceptance",
        "Naming Scheme",
        "Instance",
        "Hours of Operation",
        "Holidays",
        "Phone Number Mappings",
        "Agent Status",
        "Additional Notes",
      ];

      // Loop through each sheet and process accordingly
      for (const sheetName of sheets) {
        const sheet = fileData.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        const columnNames = jsonData[1];
        const dataRows = jsonData.slice(2);
        var rowIndex;

        const formattedData = dataRows.map((row, rowIndex) => {
          let obj = {};
          columnNames.forEach((col, index) => {
            obj[col] = row[index]; // Map each column name to its corresponding value
          });
          return obj;
        });

        // Check if the sheet name is in the exclusion list
        if (exclusionList.includes(sheetName)) {
          continue; // Skip to the next iteration of the loop
        }

        addProgressMessage(`Processing sheet: ${sheetName}`, "info");

        // Switch based on the sheet name - keeping all the original processing logic
        switch (sheetName) {
          case "Prompts":
            for (const data of formattedData) {
              // Skip rows with no data
              if (
                Object.values(data).every(
                  (value) =>
                    value === null || value === undefined || value === ""
                )
              ) {
                continue; // Skip empty rows
              }

              const requiredFields = [
                "Name",
                "Description",
                "Language",
                "Verbiage",
              ];

              // Only English Prompts are allowed in the automation for now
              if (
                data["Language"] &&
                data["Language"].trim() === "English (US)"
              ) {
                // Validation check for required fields
                if (
                  requiredFields.some(
                    (field) => data[field] === undefined || data[field] === null
                  )
                ) {
                  addProgressMessage(
                    `Error: Missing required Prompts data in row: ${JSON.stringify(data)}`,
                    "error"
                  );
                  continue; // Skip to the next row if any data is missing
                }
                try {
                  await createPrompt(
                    {
                      name: data["Name"],
                      description: data["Description"],
                      verbiage: data["Verbiage"],
                    },
                    soapCredentials.username,
                    soapCredentials.password,
                    addProgressMessage,
                    sheetName,
                    rowIndex
                  );
                } catch (error) {
                  addProgressMessage(`Prompts error: ${error}`, "error");
                }
              }
            }
            break;

          // Keep all other cases from original code but with updated progress logging
          case "Queues":
          case "User Profiles":
          case "Users":
          case "Dispositions":
          case "Reason Codes":
          case "Custom Variables":
            addProgressMessage(`Processing ${sheetName} - implementation needed`, "warning");
            break;

          default:
            addProgressMessage(`No handler for sheet: ${sheetName}`, "warning");
            break;
        }
      }

      addProgressMessage("Spreadsheet processing completed!", "success");
    } catch (error) {
      console.error("Error making script calls:", error);
      addProgressMessage(`Error making script calls: ${error.message}`, "error");
    } finally {
      setLoading(false); // End loading state
    }
  };

  // Function to get message style based on type
  const getMessageStyle = (type) => {
    switch (type) {
      case "error":
        return { color: "#dc3545" }; // Red
      case "success":
        return { color: "#28a745" }; // Green
      case "warning":
        return { color: "#ffc107", fontStyle: "italic" }; // Yellow/Orange
      case "info":
      default:
        return { color: "#007bff" }; // Blue
    }
  };

  return (
    <div
      style={{
        padding: "20px",
        fontFamily: "Arial, sans-serif",
        maxWidth: "800px",
        margin: "auto",
      }}
    >
      <h1
        style={{
          color: "#333",
          borderBottom: "2px solid #28a745",
          paddingBottom: "10px",
        }}
      >
        Five9 Spreadsheet Processor
      </h1>
      <div
        style={{
          backgroundColor: "#e2f0d9",
          border: "1px solid #c3e6cb",
          borderRadius: "5px",
          padding: "10px",
          marginBottom: "20px",
        }}
      >
        <strong>Important:</strong> Before proceeding with the upload, ensure
        the file is saved without macros enabled.
        <p style={{ margin: "5px 0 0 0", fontSize: "0.9em" }}>
          The macros on this document are for <strong>internal</strong> use
          only.
        </p>
        <ul style={{ margin: "10px 0 0 20px", padding: 0, fontSize: "0.9em" }}>
          <li>
            To do this, go to File -> Save As -> Name the file whatever you
            would like -> Ensure the file extension is <strong>.xlsx</strong>{" "}
            and not <strong>.xlsm</strong>. Doing this will automatically
            disable the macros.
          </li>
        </ul>
      </div>

      <div
        style={{
          marginBottom: "20px",
          padding: "15px",
          border: "1px solid #b8daff",
          borderRadius: "5px",
          backgroundColor: "#e0f2ff",
        }}
      >
        <p style={{ margin: "0 0 10px 0" }}>
          <strong>Current Credentials:</strong>
        </p>
        <p style={{ margin: "0" }}>
          Username:{" "}
          <span style={{ fontWeight: "bold" }}>{soapCredentials?.username || "N/A"}</span>
        </p>
        <p style={{ margin: "0" }}>
          Password:{" "}
          <span style={{ fontWeight: "bold" }}>
            {soapCredentials?.password ? "********" : "N/A"}
          </span>
        </p>
      </div>

      <div style={{ marginBottom: "20px" }}>
        <input
          type="file"
          accept=".xlsx, .xls"
          onChange={handleFileUpload}
          style={{
            padding: "8px",
            border: "1px solid #ccc",
            borderRadius: "4px",
            marginRight: "10px",
          }}
        />
        <button
          onClick={processSheetData}
          disabled={loading || !fileData}
          style={{
            padding: "10px 20px",
            backgroundColor: "#007bff",
            color: "white",
            border: "none",
            borderRadius: "5px",
            cursor: loading || !fileData ? "not-allowed" : "pointer",
            opacity: loading || !fileData ? 0.6 : 1,
            transition: "background-color 0.3s ease",
          }}
        >
          {loading ? "Processing..." : "Run Script"}
        </button>
      </div>

      {loading && (
        <div
          style={{
            padding: "10px",
            backgroundColor: "#fff3cd",
            border: "1px solid #ffeaa7",
            borderRadius: "4px",
            marginBottom: "20px",
            textAlign: "center",
          }}
        >
          <strong>Processing...</strong> Please wait while the script runs.
        </div>
      )}

      <div
        style={{
          border: "1px solid #dee2e6",
          borderRadius: "5px",
          backgroundColor: "#f8f9fa",
        }}
      >
        <h2
          style={{
            margin: "0",
            padding: "10px",
            backgroundColor: "#e9ecef",
            borderBottom: "1px solid #dee2e6",
            borderTopLeftRadius: "5px",
            borderTopRightRadius: "5px",
            fontSize: "1.2em",
          }}
        >
          Progress Log:
        </h2>
        <div
          style={{
            maxHeight: "400px",
            overflowY: "auto",
            padding: "10px",
          }}
        >
          {progress.length > 0 ? (
            <ul style={{ margin: 0, padding: 0, listStyleType: "none" }}>
              {progress.map((item, index) => (
                <li
                  key={index}
                  style={{
                    ...getMessageStyle(item.type),
                    marginBottom: "5px",
                    padding: "5px",
                    fontSize: "0.9em",
                  }}
                >
                  <strong>[{item.timestamp}]</strong> {item.message}
                </li>
              ))}
            </ul>
          ) : (
            <p style={{ margin: 0, color: "#6c757d", fontStyle: "italic" }}>
              No messages yet. Upload a file and run the script to see progress.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SpreadsheetProcessor;
